import { StockInfo, Trade } from '../../../shared/types/game.types';
import { apiGet, apiPost, apiPut } from '../../../utils/apiClient';
import { convertServerDataToChartData } from '../../stock-data/utils/dataConverter';
import { EnhancedTrade, TradingNote, TradingSession } from '../types';

// 매매내역 캐시 구조
interface TradingHistoryCacheData {
  sessions: TradingSession[];
  total: number;
  isLimited?: boolean;
  maxAllowed?: number;
  timestamp: number;
}

const tradingHistoryCache = new Map<string, TradingHistoryCacheData>();
const CACHE_DURATION = 1 * 60 * 1000; // 1분 (랭킹과 동일)

/**
 * 매매내역 캐시 무효화
 * @param subjectKey 조회 대상 키 (지정 시 해당 사용자 캐시만 무효화, 없으면 전체 무효화)
 */
export const invalidateTradingHistoryCache = (subjectKey?: string | number) => {
  if (subjectKey !== undefined && subjectKey !== null) {
    // 특정 사용자의 캐시만 무효화
    const keysToDelete: string[] = [];
    tradingHistoryCache.forEach((_, key) => {
      if (key.startsWith(`${subjectKey}_`)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => tradingHistoryCache.delete(key));
    console.log(
      `🗑️ 매매내역 캐시 무효화 (subject: ${subjectKey}, ${keysToDelete.length}개 항목)`
    );
  } else {
    // 전체 캐시 무효화
    tradingHistoryCache.clear();
    console.log('🗑️ 매매내역 캐시 전체 무효화');
  }
};

interface CreateTradingHistoryRequest {
  stockCode: string;
  stockName?: string;
  startDate: Date | string;
  endDate: Date | string;
  startAccountBalance: number;
  endAccountBalance: number;
  tradingDetails: Array<{
    stockCode: string;
    tradingDate: Date | string;
    buySellFlag: boolean;
    volume: number;
    price: number;
  }>;
}

class TradingHistoryService {
  // 사용자의 모든 거래 세션 조회
  async getTradingSessions(
    apiBaseUrl: string,
    viewerUserKey: number,
    targetUserKey: number | undefined,
    page = 1,
    limit = 10,
    forceRefresh = false
  ): Promise<{
    sessions: TradingSession[];
    total: number;
    isLimited?: boolean;
    maxAllowed?: number;
  }> {
    const subjectKey = targetUserKey ?? viewerUserKey;
    const cacheKey = `${subjectKey}_${page}_${limit}`;

    // 캐시 확인
    if (!forceRefresh && tradingHistoryCache.has(cacheKey)) {
      const cached = tradingHistoryCache.get(cacheKey)!;
      if (Date.now() - cached.timestamp < CACHE_DURATION) {
        // 이메일 로그 제거 (민감 정보 보호)
        console.log(
          `✅ 클라이언트 캐시에서 매매내역 반환 (page: ${page}, limit: ${limit})`
        );
        return {
          sessions: cached.sessions,
          total: cached.total,
          isLimited: cached.isLimited,
          maxAllowed: cached.maxAllowed,
        };
      }
    }

    // 이메일 로그 제거 (민감 정보 보호)
    console.log(
      `🌐 서버에서 매매내역 데이터 조회 (page: ${page}, limit: ${limit})`
    );

    try {
      // 백엔드 응답: { tradingHistories: [], meta: { total, ... } }
      // - 본인 조회: email 파라미터 없이 JWT로 식별
      // - 타인 조회: userKey로 조회 (email 노출 방지)
      const params: any = { page, limit };
      if (typeof targetUserKey === 'number') {
        params.userKey = targetUserKey;
      }
      const response = await apiGet<any>(`${apiBaseUrl}/api/trading-history`, params);

      // 응답이 배열인지 객체인지 확인
      let tradingHistories: any[] = [];
      let total = 0;

      if (Array.isArray(response)) {
        // 배열로 반환된 경우 (하위 호환성)
        tradingHistories = response;
        total = response.length;
      } else if (response && typeof response === 'object') {
        if ('tradingHistories' in response) {
          tradingHistories = response.tradingHistories || [];
        } else if ('data' in response && Array.isArray(response.data)) {
          // UserController의 응답 구조가 { data: [], meta: {} }인 경우
          // 하지만 getTradingHistories API는 { tradingHistories: [], meta: {} }를 반환하도록 수정됨
          // 따라서 이 분기보다는 위 분기가 우선됨
          // 만약 ResponseInterceptor가 data 필드만 추출해서 줄 경우
          // { tradingHistories: [], meta: {} } 전체가 data 필드일 수 있음.
          // 여기서는 안전하게 tradingHistories 필드를 확인
        }

        if ('meta' in response && typeof response.meta?.total === 'number') {
          total = response.meta.total;
        } else {
          total = tradingHistories.length;
        }
      }

      const isLimited = response?.meta?.isLimited || false;
      const maxAllowed = response?.meta?.maxAllowed || total;

      console.log('📥 거래 세션 조회 응답:', {
        responseType: Array.isArray(response) ? 'array' : 'object',
        tradingHistoriesCount: tradingHistories.length,
        total,
        isLimited,
        maxAllowed,
      });

      if (!tradingHistories || tradingHistories.length === 0) {
        return { sessions: [], total: 0, isLimited, maxAllowed };
      }

      // TradingHistory를 TradingSession으로 변환
      const sessions: TradingSession[] = tradingHistories.map(history => {
        const details = history.tradingDetails || [];

        const trades: EnhancedTrade[] = details.map((detail: any) => {
          const tradingDate = new Date(detail.tradingDate);
          const timestamp =
            tradingDate.getTime() + (detail.tradingSeq - 1) * 60000;

          // 메모 데이터 매핑
          let note: TradingNote | undefined = undefined;
          if (detail.tradingNote) {
            note = {
              id: String(detail.tradingNote.noteId),
              tradeId: `detail-${detail.detailId}`,
              entryReason: detail.tradingNote.entryReason || '',
              riskManagement: detail.tradingNote.riskManagement,
              confidence: detail.tradingNote.confidence ?? undefined,
              emotion: detail.tradingNote.emotion ?? undefined,
              exitReason: detail.tradingNote.exitReason,
              lessonLearned: detail.tradingNote.lessonLearned,
              createdAt: new Date(detail.tradingNote.createDate),
              updatedAt: detail.tradingNote.updateDate
                ? new Date(detail.tradingNote.updateDate)
                : new Date(detail.tradingNote.createDate),
            };
          }

          return {
            id: `detail-${detail.detailId}`,
            sessionId: String(history.historyId),
            type: detail.buySellFlag ? 'buy' : 'sell',
            price: Number(detail.price),
            quantity: detail.volume,
            timestamp,
            candleIndex: 0,
            profit: undefined,
            note,
          };
        });

        const initialBalance = Number(history.startAccountBalance);
        const finalBalance = Number(history.endAccountBalance);
        const totalProfit = finalBalance - initialBalance;
        const profitRate =
          initialBalance > 0 ? (totalProfit / initialBalance) * 100 : 0;

        return {
          id: String(history.historyId),
          userEmail: '',
          startTime: new Date(history.startDate),
          endTime: new Date(history.endDate),
          createDate: history.createDate
            ? new Date(history.createDate)
            : new Date(history.startDate),
          stockCode: history.stockCode,
          stockName: history.stockName,
          startDate: history.startDate,
          endDate: history.endDate,
          chartHistory: [],
          trades,
          initialBalance,
          finalBalance,
          totalProfit,
          profitRate,
        };
      });

      const result = { sessions, total, isLimited, maxAllowed };

      // 캐시에 저장
      tradingHistoryCache.set(cacheKey, {
        sessions,
        total,
        isLimited,
        maxAllowed,
        timestamp: Date.now(),
      });
      console.log('💾 매매내역 데이터를 클라이언트 캐시에 저장');

      return result;
    } catch (error: any) {
      if (
        error.message?.includes('404') ||
        error.message?.includes('찾을 수 없')
      ) {
        return { sessions: [], total: 0 };
      }
      throw error;
    }
  }

  // 차트 데이터만 로드하는 함수
  async loadChartDataForSession(
    apiBaseUrl: string,
    session: TradingSession
  ): Promise<TradingSession> {
    // 이미 차트 데이터가 있으면 그대로 반환
    if (session.chartHistory && session.chartHistory.length > 0) {
      return session;
    }

    // 필수 정보 확인
    if (!session.stockCode) {
      throw new Error('종목 코드가 없어 차트 데이터를 불러올 수 없습니다.');
    }

    if (!session.startDate) {
      throw new Error('시작 날짜가 없어 차트 데이터를 불러올 수 없습니다.');
    }

    // 차트 데이터 로드
    let chartHistory: any[] = [];
    const startDateStr = session.startDate;
    const endDateStr = session.endDate || '';

    console.log('📊 차트 데이터 로드 시작:', {
      stockCode: session.stockCode,
      startDate: startDateStr,
      endDate: endDateStr,
    });

    let actualStartDate = startDateStr;
    let actualEndDate: string | undefined = endDateStr;

    // startDate와 endDate가 같거나 범위가 너무 좁은 경우
    if (startDateStr === endDateStr) {
      console.warn(
        `⚠️ 시작일과 종료일이 같습니다. 게임 기간을 확장합니다. (${startDateStr})`
      );
      actualEndDate = undefined;
    }

    const requestParams: Record<string, string | number | boolean> = {
      stockCode: session.stockCode,
      startDate: actualStartDate,
    };
    if (actualEndDate) {
      requestParams.endDate = actualEndDate;
    }

    try {
      const chartDataResponse = await apiGet<any>(
        `${apiBaseUrl}/api/stock-data/get-daily-stock-data`,
        requestParams
      );

      console.log('📦 차트 데이터 응답:', {
        hasResponse: !!chartDataResponse,
        responseType: typeof chartDataResponse,
        isArray: Array.isArray(chartDataResponse),
        responseKeys:
          chartDataResponse && !Array.isArray(chartDataResponse)
            ? Object.keys(chartDataResponse)
            : [],
        responseLength: Array.isArray(chartDataResponse)
          ? chartDataResponse.length
          : 'not array',
      });

      if (!chartDataResponse) {
        throw new Error('차트 데이터 응답이 없습니다.');
      }

      // 응답이 배열인 경우 (ResponseInterceptor가 배열을 직접 반환)
      let chartDataArray: any[];
      let stockCode: string = session.stockCode;
      let stockName: string = '';

      if (Array.isArray(chartDataResponse)) {
        // 배열로 직접 반환된 경우
        chartDataArray = chartDataResponse;
      } else if (
        chartDataResponse.data &&
        Array.isArray(chartDataResponse.data)
      ) {
        // { stockCode, stockName, data: [...] } 형태
        chartDataArray = chartDataResponse.data;
        stockCode = chartDataResponse.stockCode || stockCode;
        stockName = chartDataResponse.stockName || '';
      } else {
        console.error('❌ 차트 데이터 형식 오류:', {
          response: chartDataResponse,
          dataType: typeof chartDataResponse,
          isArray: Array.isArray(chartDataResponse),
        });
        throw new Error('차트 데이터 형식이 올바르지 않습니다.');
      }

      chartHistory = convertServerDataToChartData(chartDataArray);

      console.log('✅ 차트 데이터 로드 완료:', {
        stockCode,
        dataLength: chartHistory.length,
      });

      if (chartHistory.length === 0) {
        throw new Error('차트 데이터가 비어있습니다.');
      }
    } catch (error) {
      console.error('❌ 차트 데이터 로드 실패:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : '차트 데이터를 불러오는데 실패했습니다.';
      throw new Error(errorMessage);
    }

    // 세션에 차트 데이터 추가
    return {
      ...session,
      chartHistory,
    };
  }

  // 특정 세션 상세 조회
  async getSessionDetail(
    apiBaseUrl: string,
    sessionId: string
  ): Promise<TradingSession> {
    // 백엔드 응답: { historyId, stockCode, stockName, ..., tradingDetails: [...] }
    const response = await apiGet<{
      historyId: number;
      stockCode: string;
      stockName: string;
      email: string;
      startDate: Date | string;
      endDate: Date | string;
      startAccountBalance: number | string;
      endAccountBalance: number | string;
      createDate?: Date | string;
      tradingDetails: any[];
    }>(`${apiBaseUrl}/api/trading-history/${sessionId}`);

    console.log('📥 세션 상세 조회 응답:', {
      historyId: response.historyId,
      tradingDetailsCount: response.tradingDetails?.length || 0,
    });

    if (!response) {
      throw new Error('거래 세션을 찾을 수 없습니다.');
    }

    // tradingDetails를 EnhancedTrade 형식으로 변환
    const trades: EnhancedTrade[] = (response.tradingDetails || []).map(
      detail => {
        const tradingDate = new Date(detail.tradingDate);
        // tradingDate와 tradingSeq를 조합하여 timestamp 생성
        const timestamp =
          tradingDate.getTime() + (detail.tradingSeq - 1) * 60000; // 분 단위로 간격

        // 메모 데이터 매핑
        let note: TradingNote | undefined = undefined;
        if (detail.tradingNote) {
          note = {
            id: String(detail.tradingNote.noteId),
            tradeId: `detail-${detail.detailId}`,
            entryReason: detail.tradingNote.entryReason || '',
            riskManagement: detail.tradingNote.riskManagement,
            confidence: detail.tradingNote.confidence ?? undefined,
            emotion: detail.tradingNote.emotion ?? undefined,
            exitReason: detail.tradingNote.exitReason,
            lessonLearned: detail.tradingNote.lessonLearned,
            createdAt: new Date(detail.tradingNote.createDate),
            updatedAt: detail.tradingNote.updateDate
              ? new Date(detail.tradingNote.updateDate)
              : new Date(detail.tradingNote.createDate),
          };
        }

        return {
          id: `detail-${detail.detailId}`,
          sessionId: String(response.historyId),
          type: detail.buySellFlag ? 'buy' : 'sell',
          price: Number(detail.price),
          quantity: detail.volume,
          timestamp,
          candleIndex: 0, // TODO: 차트 데이터와 매칭 필요
          profit: undefined, // TODO: 수익률 계산 필요
          note,
        };
      }
    );

    // 수익률 계산
    const initialBalance = Number(response.startAccountBalance);
    const finalBalance = Number(response.endAccountBalance);
    const totalProfit = finalBalance - initialBalance;
    const profitRate =
      initialBalance > 0 ? (totalProfit / initialBalance) * 100 : 0;

    // 차트 데이터 로드
    // 게임이 실제로 진행된 날짜 범위를 사용하여 차트 데이터를 가져옴
    let chartHistory: any[] = [];
    try {
      // 날짜 범위 확인 및 디버깅
      const startDateStr =
        typeof response.startDate === 'string'
          ? response.startDate
          : new Date(response.startDate).toISOString().split('T')[0];
      const endDateStr =
        typeof response.endDate === 'string'
          ? response.endDate
          : new Date(response.endDate).toISOString().split('T')[0];

      console.log('📊 차트 데이터 로드 시작:', {
        stockCode: response.stockCode,
        startDate: startDateStr,
        endDate: endDateStr,
        startDateType: typeof response.startDate,
        endDateType: typeof response.endDate,
      });

      // 날짜 범위가 같은 경우, 게임이 진행된 전체 기간을 포함하도록 조정
      // 게임은 최대 50개의 캔들이 진행되므로, 시작일부터 최소 50일 범위를 포함
      let actualStartDate = startDateStr;
      let actualEndDate: string | undefined = endDateStr;

      // startDate와 endDate가 같거나 범위가 너무 좁은 경우
      if (startDateStr === endDateStr) {
        console.warn(
          `⚠️ 시작일과 종료일이 같습니다. 게임 기간을 확장합니다. (${startDateStr})`
        );
        // 시작일부터 최소 50일 범위로 확장 (또는 전체 데이터 사용)
        actualEndDate = undefined; // endDate를 제거하여 전체 데이터 가져오기
      }

      // 게임이 실제로 진행된 날짜 범위로 차트 데이터 가져오기
      const requestParams: Record<string, string | number | boolean> = {
        stockCode: response.stockCode,
        startDate: actualStartDate,
      };
      if (actualEndDate) {
        requestParams.endDate = actualEndDate;
      }

      const chartDataResponse = await apiGet<{
        stockCode: string;
        stockName: string;
        data: any[];
      }>(`${apiBaseUrl}/api/stock-data/get-daily-stock-data`, requestParams);

      if (chartDataResponse && chartDataResponse.data) {
        chartHistory = convertServerDataToChartData(chartDataResponse.data);

        // 첫 번째와 마지막 캔들의 날짜 확인
        const firstCandleDate =
          chartHistory.length > 0
            ? new Date(chartHistory[0].timestamp).toISOString().split('T')[0]
            : null;
        const lastCandleDate =
          chartHistory.length > 0
            ? new Date(chartHistory[chartHistory.length - 1].timestamp)
                .toISOString()
                .split('T')[0]
            : null;

        console.log('✅ 차트 데이터 로드 완료:', {
          stockCode: chartDataResponse.stockCode,
          dataLength: chartHistory.length,
          rawDataLength: chartDataResponse.data.length,
          requestedStartDate: actualStartDate,
          requestedEndDate: actualEndDate,
          firstCandleDate,
          lastCandleDate,
          matchesStartDate: firstCandleDate === actualStartDate,
          matchesEndDate: lastCandleDate === actualEndDate,
        });

        // 첫 번째 캔들이 startDate와 일치하지 않으면 경고
        if (
          firstCandleDate &&
          actualStartDate &&
          firstCandleDate !== actualStartDate
        ) {
          console.warn(
            '⚠️ 차트 데이터의 첫 번째 날짜가 요청한 startDate와 일치하지 않습니다:',
            {
              requestedStartDate: actualStartDate,
              actualFirstCandleDate: firstCandleDate,
            }
          );
        }

        // 데이터가 하나만 있는 경우 경고
        if (chartHistory.length === 1) {
          console.warn(
            '⚠️ 차트 데이터가 하나만 있습니다. 날짜 범위를 확인해주세요.',
            {
              startDate: actualStartDate,
              endDate: actualEndDate,
              stockCode: response.stockCode,
            }
          );
        }
      }
    } catch (error) {
      console.warn('⚠️ 차트 데이터 로드 실패:', error);
      // 차트 데이터가 없어도 세션은 표시 가능하도록 빈 배열 유지
    }

    const session: TradingSession = {
      id: String(response.historyId),
      userEmail: '',
      startTime: new Date(response.startDate),
      endTime: new Date(response.endDate),
      createDate: response.createDate
        ? new Date(response.createDate)
        : new Date(response.startDate),
      stockCode: response.stockCode,
      stockName: response.stockName,
      startDate:
        typeof response.startDate === 'string'
          ? response.startDate
          : response.startDate.toISOString().split('T')[0],
      endDate:
        typeof response.endDate === 'string'
          ? response.endDate
          : response.endDate.toISOString().split('T')[0],
      chartHistory,
      trades,
      initialBalance,
      finalBalance,
      totalProfit,
      profitRate,
    };

    console.log('✅ 변환된 세션 상세:', {
      sessionId: session.id,
      tradesCount: session.trades.length,
    });

    return session;
  }

  // 게임 종료 시 거래 내역 저장
  async saveGameTradingHistory(
    apiBaseUrl: string,
    stockInfo: StockInfo,
    startBalance: number,
    endBalance: number,
    trades: Trade[]
  ): Promise<{
    unlockedTitles: Array<{ code: string; name: string; icon: string }>;
    unlockedAchievements: Array<{ code: string; name: string; icon: string }>;
  } | null> {
    console.log('📝 saveGameTradingHistory 호출:', {
      stockCode: stockInfo.stockCode,
      stockName: stockInfo.stockName,
      startBalance,
      endBalance,
      tradesLength: trades?.length || 0,
    });

    // 거래 내역이 없어도 게임 세션은 저장 (거래 없이 게임 종료한 경우)
    if (!trades || trades.length === 0) {
      console.log('⚠️ 거래 내역이 없지만 게임 세션은 저장합니다.');
      // trades가 빈 배열이어도 계속 진행
    }

    // Trade[]를 CreateTradingDetailHistoryDto[]로 변환
    // trades가 없으면 빈 배열 사용
    const tradingDetails = (trades || []).map(trade => {
      const tradeDate =
        trade.time instanceof Date ? trade.time : new Date(trade.time);

      // trade.type이 'buy' 또는 'sell'인지 확인 ('매수', '매도'도 처리)
      const tradeTypeLower = trade.type.toLowerCase();
      const isBuy = tradeTypeLower === 'buy' || tradeTypeLower === '매수';

      console.log('🔍 거래 타입 확인:', {
        originalType: trade.type,
        typeOf: typeof trade.type,
        isBuy,
        tradeTime: tradeDate,
        tradeDateString: tradeDate.toISOString().split('T')[0],
      });

      return {
        stockCode: stockInfo.stockCode,
        tradingDate: tradeDate.toISOString().split('T')[0], // YYYY-MM-DD 형식
        buySellFlag: isBuy, // 'buy' -> true, 'sell' -> false
        volume: trade.size,
        price: trade.price,
      };
    });

    // 게임이 실제로 진행된 날짜 범위 사용
    // gameStartDate가 반드시 있어야 함 (게임 시작 시점의 첫 번째 봉 날짜)
    // gameStartDate가 없으면 에러를 발생시키거나, gameData의 첫 번째 캔들 날짜를 사용해야 함
    if (!stockInfo.gameStartDate) {
      console.error('❌ gameStartDate가 설정되지 않았습니다!', {
        stockInfo,
        message: '게임 시작 시점에 gameStartDate가 설정되어야 합니다.',
      });
      // gameStartDate가 없으면 저장하지 않음 (또는 에러 발생)
      throw new Error(
        'gameStartDate가 설정되지 않았습니다. 게임 시작 시점의 날짜 정보가 필요합니다.'
      );
    }

    const actualStartDate = stockInfo.gameStartDate;
    // gameEndDate가 있으면 사용, 없으면 endDate 사용 (하위 호환성)
    const actualEndDate = stockInfo.gameEndDate || stockInfo.endDate;

    // gameStartDate가 없으면 경고
    if (!stockInfo.gameStartDate) {
      console.warn(
        '⚠️ gameStartDate가 설정되지 않았습니다! startDate를 사용합니다:',
        {
          stockInfo,
          actualStartDate,
        }
      );
    }

    // 첫 거래일과 게임 시작일이 다르면 경고
    if (trades.length > 0) {
      const firstTradeDate = (
        trades[0].time instanceof Date
          ? trades[0].time
          : new Date(trades[0].time)
      )
        .toISOString()
        .split('T')[0];
      if (actualStartDate && firstTradeDate !== actualStartDate) {
        console.warn('⚠️ 첫 거래일과 게임 시작일이 다릅니다:', {
          gameStartDate: actualStartDate,
          firstTradeDate,
          message: '게임 시작일부터 차트가 표시되어야 합니다.',
        });
      }
    }

    console.log('📅 날짜 정보 확인:', {
      stockInfo: {
        startDate: stockInfo.startDate,
        endDate: stockInfo.endDate,
        gameStartDate: stockInfo.gameStartDate,
        gameEndDate: stockInfo.gameEndDate,
      },
      actualStartDate,
      actualEndDate,
      firstTradeDate:
        trades.length > 0
          ? (trades[0].time instanceof Date
              ? trades[0].time
              : new Date(trades[0].time)
            )
              .toISOString()
              .split('T')[0]
          : null,
      lastTradeDate:
        trades.length > 0
          ? (trades[trades.length - 1].time instanceof Date
              ? trades[trades.length - 1].time
              : new Date(trades[trades.length - 1].time)
            )
              .toISOString()
              .split('T')[0]
          : null,
    });

    const request: CreateTradingHistoryRequest = {
      stockCode: stockInfo.stockCode,
      stockName: stockInfo.stockName,
      startDate: actualStartDate,
      endDate: actualEndDate,
      startAccountBalance: startBalance,
      endAccountBalance: endBalance,
      tradingDetails: tradingDetails,
    };

    console.log('📤 API 요청 데이터:', {
      stockCode: request.stockCode,
      stockName: request.stockName,
      startDate: request.startDate,
      endDate: request.endDate,
      startDateType: typeof request.startDate,
      endDateType: typeof request.endDate,
      startAccountBalance: request.startAccountBalance,
      endAccountBalance: request.endAccountBalance,
      tradingDetailsCount: request.tradingDetails.length,
      tradingDetails: request.tradingDetails.map((detail, index) => ({
        index,
        stockCode: detail.stockCode,
        tradingDate: detail.tradingDate,
        buySellFlag: detail.buySellFlag,
        volume: detail.volume,
        price: detail.price,
      })),
    });

    try {
      const response = await apiPost<{
        historyId: number;
        unlockedAchievements?: {
          unlockedTitles: Array<{ code: string; name: string; icon: string }>;
          unlockedAchievements: Array<{
            code: string;
            name: string;
            icon: string;
          }>;
        };
      }>(`${apiBaseUrl}/api/trading-history`, request);
      console.log('✅ 거래 내역 저장 완료:', {
        historyId: response.historyId,
        unlockedAchievements: response.unlockedAchievements,
      });

      // 매매내역 저장 후 해당 사용자의 캐시 무효화
      invalidateTradingHistoryCache();

      // 획득한 칭호 반환
      return response.unlockedAchievements || null;
    } catch (error) {
      console.error('❌ 거래 내역 저장 실패:', error);
      console.error('❌ 에러 상세:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      // 에러가 발생해도 게임 종료는 계속 진행
      throw error; // 에러를 다시 throw하여 호출자가 확인할 수 있도록
    }
  }

  // 매매노트 저장
  async saveTradingNote(
    apiBaseUrl: string,
    tradeId: string,
    noteData: Partial<TradingNote>
  ): Promise<TradingNote> {
    // tradeId 형식: "detail-123" -> detailId: 123
    let detailId: number | undefined = undefined;
    const match = tradeId.match(/^detail-(\d+)$/);
    if (match) {
      detailId = parseInt(match[1], 10);
    } else {
      // 숫자만 있는 경우도 처리
      detailId = parseInt(tradeId, 10);
    }

    const result = await apiPost<TradingNote>(
      `${apiBaseUrl}/api/trading-review/notes`,
      {
        detailId,
        tradeId, // 하위 호환성을 위해 유지
        ...noteData,
      }
    );

    // 메모 저장 후 캐시 무효화 (메모가 포함된 매매내역이 변경되었으므로)
    invalidateTradingHistoryCache();

    return result;
  }

  // 매매노트 수정
  async updateTradingNote(
    apiBaseUrl: string,
    noteId: string,
    noteData: Partial<TradingNote>
  ): Promise<TradingNote> {
    const result = await apiPut<TradingNote>(
      `${apiBaseUrl}/api/trading-review/notes/${noteId}`,
      noteData
    );

    // 메모 수정 후 캐시 무효화
    invalidateTradingHistoryCache();

    return result;
  }
}

export const tradingHistoryService = new TradingHistoryService();
